/**
 * Query Parser - Maps natural language questions to Segmentation API calls
 * Rule-based parsing for common analytics questions
 */

import type { SegmentationQuery } from './mixpanel';

export type QueryType =
  | 'top_days'      // "Top 10 days for signups"
  | 'by_country'    // "Sessions by country"
  | 'by_city'       // "DAU by city"
  | 'trend'         // "DAU over time"
  | 'compare'       // "iOS vs Android"
  | 'anomaly'       // "Any anomalies in DAU?"
  | 'top_users'     // "Who are the top 10 users?"
  | 'insight'       // "What insights can you give me?"
  | 'conversation'  // General analytics question
  | 'unknown';      // Couldn't parse

export interface ParsedQuery {
  type: QueryType;
  event: string;
  metric: 'unique' | 'general' | 'average';
  dimension?: 'country' | 'city' | 'region' | 'platform';
  limit?: number;
  fromDate: string;
  toDate: string;
  originalQuestion: string;
}

// Event name mappings for natural language
const EVENT_MAPPINGS: Record<string, string> = {
  'session': '$ae_session',
  'sessions': '$ae_session',
  'signup': '$ae_session', // First session = signup approximation
  'signups': '$ae_session',
  'dau': '$ae_session',
  'mau': '$ae_session',
  'active users': '$ae_session',
  'users': '$ae_session',
  'chat': 'Chat Viewed',
  'chats': 'Chat Viewed',
  'messages': 'Chat Viewed',
  'profile': 'View Profile',
  'profiles': 'View Profile',
  'search': 'Search',
  'searches': 'Search',
  'connect': 'Connect With Member',
  'connections': 'Connect With Member',
  'wallet': 'Wallet Tab Viewed',
  'event': 'Event Viewed',
  'events': 'Event Viewed',
};

// Format date as YYYY-MM-DD
function formatDate(date: Date): string {
  return date.toISOString().split('T')[0];
}

// Get date N days ago
function daysAgo(n: number): Date {
  const date = new Date();
  date.setDate(date.getDate() - n);
  return date;
}

// Get start of year
function startOfYear(): Date {
  const date = new Date();
  date.setMonth(0, 1);
  return date;
}

/**
 * Parse a natural language question into a structured query
 */
export function parseQuestion(question: string): ParsedQuery {
  const q = question.toLowerCase().trim();
  const now = new Date();
  const today = formatDate(now);
  const isEarlyJanuary = now.getMonth() === 0 && now.getDate() <= 15;

  // Default result
  const result: ParsedQuery = {
    type: 'unknown',
    event: '$ae_session',
    metric: 'unique',
    fromDate: formatDate(daysAgo(30)),
    toDate: today,
    originalQuestion: question,
  };

  // Detect time range
  const currentYear = new Date().getFullYear().toString();
  if (q.includes('year') || q.includes(currentYear) || q.includes('all time')) {
    result.fromDate = formatDate(startOfYear());
  } else if (q.includes('90 day') || q.includes('3 month')) {
    result.fromDate = formatDate(daysAgo(90));
  } else if (q.includes('7 day') || q.includes('week')) {
    result.fromDate = formatDate(daysAgo(7));
  }

  // Detect event type from question
  for (const [keyword, eventName] of Object.entries(EVENT_MAPPINGS)) {
    if (q.includes(keyword)) {
      result.event = eventName;
      break;
    }
  }

  // Pattern: "insight", "insights", "recommendations", "suggestions", "analyze"
  if (q.includes('insight') || q.includes('recommend') || q.includes('suggest') ||
      q.includes('analyze') || q.includes('analysis') || q.includes('what should')) {
    result.type = 'insight';
    return result;
  }

  // Pattern: "top N users" or "top users"
  const topUsersMatch = q.match(/top\s*(\d+)?\s*users?/i);
  if (topUsersMatch || q.includes('leaderboard') || q.includes('most active')) {
    result.type = 'top_users';
    result.limit = topUsersMatch?.[1] ? parseInt(topUsersMatch[1], 10) : 10;
    // Top users uses Export API, limit to 30 days for performance
    result.fromDate = formatDate(daysAgo(30));
    return result;
  }

  // Pattern: "anomaly", "anomalies", "spike", "drop", "unusual"
  if (q.includes('anomal') || q.includes('spike') || q.includes('drop') ||
      q.includes('unusual') || q.includes('outlier')) {
    if (q.includes('why') || q.includes('what') || q.includes('explain') || q.includes('cause')) {
      result.type = 'conversation';
      return result;
    }
    result.type = 'anomaly';
    // For anomaly detection, use 90 days by default
    result.fromDate = formatDate(daysAgo(90));
    return result;
  }

  // Pattern: "top N days for X"
  const topDaysMatch = q.match(/top\s*(\d+)?\s*days?\s*(for|of|in)?/i);
  if (topDaysMatch) {
    result.type = 'top_days';
    result.limit = topDaysMatch[1] ? parseInt(topDaysMatch[1], 10) : 10;
    // For "top days" queries, default to full year
    if (!q.includes('week') && !q.includes('month')) {
      result.fromDate = formatDate(isEarlyJanuary ? daysAgo(90) : startOfYear());
    }
    return result;
  }

  // Pattern: "by country" or "by countries"
  if (q.includes('by country') || q.includes('by countries') || q.includes('per country')) {
    result.type = 'by_country';
    result.dimension = 'country';
    return result;
  }

  // Pattern: "by city" or "by cities"
  if (q.includes('by city') || q.includes('by cities') || q.includes('per city')) {
    result.type = 'by_city';
    result.dimension = 'city';
    return result;
  }

  // NOTE: "retention by country/city" is NOT supported yet
  // Calculating retention rates segmented by geography requires cohort analysis
  // which the Segmentation API doesn't directly support. For now, these queries
  // will fall through to 'unknown' rather than misleadingly return session counts.
  // TODO: Implement via Export API + cohort calculation when needed.

  // Pattern: geographic questions
  if (q.includes('country') || q.includes('countries') || q.includes('geography') || q.includes('geo')) {
    result.type = 'by_country';
    result.dimension = 'country';
    return result;
  }

  if (q.includes('city') || q.includes('cities')) {
    result.type = 'by_city';
    result.dimension = 'city';
    return result;
  }

  // Pattern: "over time" or "trend"
  if (q.includes('over time') || q.includes('trend') || q.includes('daily')) {
    result.type = 'trend';
    return result;
  }

  // Pattern: comparison "X vs Y"
  if (q.includes(' vs ') || q.includes('compare') || q.includes('comparison')) {
    result.type = 'compare';
    result.dimension = 'platform';
    return result;
  }

  // If nothing matched but we have a recognizable metric, default to trend
  if (result.event !== '$ae_session' || q.includes('show') || q.includes('what')) {
    result.type = 'trend';
  }

  if (result.type === 'unknown') {
    result.type = 'conversation';
  }

  return result;
}

/**
 * Convert parsed query to Segmentation API query
 */
export function toSegmentationQuery(parsed: ParsedQuery): SegmentationQuery {
  const query: SegmentationQuery = {
    event: parsed.event,
    fromDate: parsed.fromDate,
    toDate: parsed.toDate,
    type: parsed.metric,
  };

  // Add dimension for breakdown queries
  if (parsed.dimension) {
    const propMap: Record<string, string> = {
      country: 'properties["mp_country_code"]',
      city: 'properties["$city"]',
      region: 'properties["$region"]',
      platform: 'properties["$os"]',
    };
    query.on = propMap[parsed.dimension];
  }

  return query;
}

/**
 * Get suggested follow-up questions based on query type
 */
export function getSuggestedFollowups(queryType: QueryType): string[] {
  switch (queryType) {
    case 'top_days':
      return ['Sessions by country', 'Any anomalies?', 'Give me insights'];
    case 'by_country':
      return ['Break down by city', 'Top 10 days this year', 'Give me insights'];
    case 'by_city':
      return ['Show by country instead', 'Top users', 'Give me insights'];
    case 'trend':
      return ['By country', 'Top 10 days', 'Give me insights'];
    case 'compare':
      return ['Sessions by country', 'Top 10 days', 'Give me insights'];
    case 'anomaly':
      return ['Top 10 days', 'Sessions by country', 'Give me insights'];
    case 'top_users':
      return ['Sessions by country', 'Any anomalies?', 'Give me insights'];
    case 'insight':
      return ['Top 10 days', 'Any anomalies?', 'Top users'];
    case 'conversation':
      return ['Top 10 days for sessions', 'Sessions by country', 'Any anomalies?'];
    default:
      return ['Top 10 days for sessions', 'Sessions by country', 'Give me insights'];
  }
}
